import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button } from "@mui/material";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import BasicDialog from "../../../components/base/BasicDialog";
import { SnackbarError } from "../../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../../components/base/SnackbarAlert/SnackbarSuccess";
import ConfirmationDialog from "../../../components/dialog/ConfirmationDialog/confirmationDialog";
import { getProductStatusId } from "../../../helpers/productStatusUtils";
import { useDefaultProductStatus } from "../../../hooks/useDefaultProductStatus";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import {
    useMassUpdateProductsMutation,
    useUpdateProductMutation,
} from "../../../services/productsService";
import { ProductCards } from "./ProductCards";
import { ProductDocuments } from "./ProductDocuments";
import { ProductNoteControl } from "./ProductNoteControl";
import { ProductParameters } from "./ProductParameters";
import { ProductPricing } from "./ProductPricing";
import { ProductTagsSelector } from "./ProductTagsSelector";
import { RelatedProductsControl } from "./RelatedProductsControl";
import { SelectedProductsTable } from "./SelectedProductsTable";
import { LoadingButton } from "@mui/lab";
import { isEmptyValue } from "../../../helpers/methods";
import { ProductHotelNoteControl } from "./ProductHotelNoteControl";

export const ProductConfigurationDialog = (props) => {
    const { t } = useTranslation();
    const { products } = props;
    const isMultipleProducts = products.length > 1;

    const {
        defaultStatus,
        isLoading: isDefaultStatusLoading,
        error: defaultStatusError,
    } = useDefaultProductStatus();

    const { openDialog: openPublishPricingDialog, closeDialog: closePublishPricingDialog } =
        useDialogBetter();

    const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);

    const mutationHandlers = {
        onError: (error) => {
            console.error("Failed to update products", error);
            toast.custom(
                () => (
                    <SnackbarError message={t("products.ProductConfigurationDialog.updateError")} />
                ),
                {
                    position: "bottom-right",
                },
            );
        },
        onSuccess: () => {
            toast.custom(
                () => (
                    <SnackbarSuccess
                        message={t("products.ProductConfigurationDialog.updatedSuccessfully")}
                    />
                ),
                {
                    position: "bottom-right",
                },
            );
            props.onClose();
        },
    };

    const { mutate: updateSingleProduct, isPending: isUpdatingSingleProduct } = useUpdateProductMutation(mutationHandlers);

    const { mutate: updateMultipleProducts, isPending: isUpdatingMultipleProducts } = useMassUpdateProductsMutation(mutationHandlers);

    const methods = useForm({
        resolver: isMultipleProducts ? null : zodResolver(productConfigurationSchema),
        defaultValues: isMultipleProducts
            ? getEmptyFormValues()
            : getInitialFormDataFromProduct(products[0], defaultStatus),
    });

    const {
        handleSubmit,
        control,
        setValue,
        watch,
        formState: { errors, isValid },
    } = methods;

    if (isDefaultStatusLoading) {
        return null;
    }

    if (defaultStatusError || !defaultStatus) {
        console.error("Default product status not found", defaultStatusError);
        return null;
    }

    const handleUpdateSingleProduct = (data) => {
        const request = {
            name_full: data.name,
            footage: data.footage,
            footage_postconstruction: data.footageFinal,
            note: data.note,
            hotel_note: data.hotel_note,
            floor: data.floor,
            num_rooms: data.num_rooms,
            product_number: data.productNumber,
            pricing: {
                vat_proc: data.pricing.vat,
                discount_max: data.pricing.maxDiscount,
                listed_net_per_m2: data.pricing.listedNetPerSqm.toString(),
                min_net_per_m2: data.pricing.minNetPerSqm.toString(),
                base_net_per_m2: data.pricing.baseNetPerSqm.toString(),
            },
            type: data.type,
            kind: data.kind,
            status: data.status,
            style: data.style,
            orientation: {
                east: data.sideEast,
                west: data.sideWest,
                north: data.sideNorth,
                south: data.sideSouth,
            },
            children: data.relatedProducts,
            tags: data.tags,
        };

        // if listedNetPerSqm pricing or vat are changed ask user if he want to publish prices
        if (
            data.pricing.listedNetPerSqm !==
                Number.parseFloat(products?.[0]?.pricing.listed_net_per_m2 || "0") ||
            data.pricing.vat !==
                (typeof products?.[0]?.pricing.vat_proc === "string"
                    ? Number.parseFloat(products?.[0]?.pricing.vat_proc || "0")
                    : products?.[0]?.pricing.vat_proc || 0) ||
            data.footage !== products?.[0]?.footage
        ) {
            setIsPublishDialogOpen(true);
            openPublishPricingDialog({
                Component: ConfirmationDialog,
                componentProps: {
                    title: t("products.ProductConfigurationDialog.publishPricing"),
                    content: t("products.ProductConfigurationDialog.publishPricingContent"),
                    onYes: () => {
                        const tempRequest = { ...request };
                        tempRequest.pricing.pricing_published = true;
                        updateSingleProduct({ productId: products[0].id, body: tempRequest });
                        closePublishPricingDialog();
                    },
                    onNo: () => {
                        updateSingleProduct({ productId: products[0].id, body: request });
                        closePublishPricingDialog();
                    },
                    showCloseButton: true,
                    onCancel : () => {
                        closePublishPricingDialog()
                        setIsPublishDialogOpen(false)
                    }
                },
            });
            return;
        }
        updateSingleProduct({ productId: products[0].id, body: request });
    };

    const isFormValidForMultipleProducts = () => watch("tags").length > 0 || multipleProductsFieldNamesToEdit.some((fieldName)=> !isEmptyValue(watch(fieldName)))

    const prepareDataToSendForMultipleProducts = (data) => {
        let dataToSend = {}
        for (const fieldName of multipleProductsFieldNamesToEdit) {
            if (!isEmptyValue(data[fieldName])) {
                dataToSend[fieldName] = data[fieldName]
            }
        }

        if (data.tags && data.tags.length > 0) {
            dataToSend.tags_to_add = data.tags 
        }

        return dataToSend
    }

    const handleUpdateMultipleProducts = (data) => {
        const request = {
            products: products.map((p) => p.id),
            update_data: prepareDataToSendForMultipleProducts(data),
            // old request body
            // update_data: {
            //     footage: data.footage,
            //     footage_postconstruction: data.footageFinal,
            //     pricing: {
            //         vat_proc: data.pricing.vat,
            //         discount_max: data.pricing.maxDiscount,
            //         listed_net_per_m2: data.pricing.listedNetPerSqm.toString(),
            //         min_net_per_m2: data.pricing.minNetPerSqm.toString(),
            //         base_net_per_m2: data.pricing.baseNetPerSqm.toString(),
            //     },
            //     kind: data.kind,
            //     type: data.type,
            //     status: data.status,
            //     orientation: {
            //         east: data.sideEast,
            //         west: data.sideWest,
            //         north: data.sideNorth,
            //         south: data.sideSouth,
            //     },
            //     tags: data.tags,
            //     note: data.note === "" ? undefined : data.note,
            // },
        };
        updateMultipleProducts(request);
    };

    const onSubmit = (data) => {
        if (isMultipleProducts) {
            handleUpdateMultipleProducts(data);
        } else {
            handleUpdateSingleProduct(data);
        }
    };

    const submitButtonText = isMultipleProducts
        ? t("products.ProductConfigurationDialog.submitPlural")
        : t("products.ProductConfigurationDialog.submitSingular");

    const selectedTagIds = watch("tags");
    const setFormTagIds = (ids) => setValue("tags", ids);
    const formRelatedProductIds = watch("relatedProducts");
    const setFormRelatedProductIds = (ids) => setValue("relatedProducts", ids);

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("products.ProductConfigurationDialog.title")}
            minWidth={800}
            maxWidth={"xl"}
            showDialogActions
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box>
                        <SelectedProductsTable products={props.products} />
                        <ProductPricing
                            isMultipleProducts={isMultipleProducts}
                            readOnly={isMultipleProducts}
                            control={control}
                            fieldNames={productPricingFieldNames}
                            setValue={setValue}
                            productId={isMultipleProducts ? undefined : props.products[0]?.id}
                        />
                        <Box display="flex" flexDirection="row">
                            <ProductParameters
                                control={control}
                                forMultipleProducts={isMultipleProducts}
                                fieldNames={productParametersFieldNames}
                                isFinalSettlement={isMultipleProducts ? false : props.products[0]?.product_final_settlement}
                                investmnetHomeId={props.products[0]?.investment?.id_invest_home}
                                readOnly={false}
                            />
                            <ProductTagsSelector
                                formTagIds={selectedTagIds}
                                setFormTagIds={setFormTagIds}
                            />
                        </Box>
                        <Box display="flex" flexDirection="row">
                            <RelatedProductsControl
                                relatedProductIds={formRelatedProductIds}
                                setRelatedProductIds={setFormRelatedProductIds}
                                readOnly={isMultipleProducts}
                                excludedProductId={products[0].id}
                            />
                            <ProductDocuments
                                product={isMultipleProducts ? undefined : products[0]}
                            />
                        </Box>
                        <Box display="flex" flexDirection="column">
                            <ProductCards product={isMultipleProducts ? undefined : products[0]} />
                        </Box>
                        <Box display="flex" flexDirection="row">
                            <Box sx={{ flex: 1 }}>
                                <ProductNoteControl control={control} fieldName="note" />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <ProductHotelNoteControl control={control} fieldName="hotel_note" />
                            </Box>                                      
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <LoadingButton
                                    variant="contained"
                                    size="large"
                                    type="submit"
                                    loading={isUpdatingSingleProduct || isUpdatingMultipleProducts}
                                    disabled={isPublishDialogOpen || (isMultipleProducts ? !isFormValidForMultipleProducts() : !isValid) }
                                >
                                    {submitButtonText}
                                </LoadingButton>
                            </Box>
                        </Box>
                    </Box>
                </form>
            </FormProvider>
        </BasicDialog>
    );
};

const productConfigurationSchema = z.object({
    name: z.string(),
    type: z.string().uuid(),
    productNumber: z.string(),
    floor: z.coerce.number().int().min(-20),
    num_rooms: z.coerce.number().int().min(1),
    style: z.string().optional(),
    status: z.string().uuid("Missing status"),
    sideEast: z.boolean(),
    sideWest: z.boolean(),
    sideNorth: z.boolean(),
    sideSouth: z.boolean(),
    footage: z.coerce.number().min(0),
    footageFinal: z.coerce.number().min(0).nullable(),
    kind: z.string().min(1, "Missing kind").uuid("Invalid kind"),
    pricing: z.object({
        vat: z.coerce.number().min(0).max(100),
        maxDiscount: z.coerce.number().min(0).max(100),
        listedNetPerSqm: z.coerce.number().min(0),
        minNetPerSqm: z.coerce.number().min(0),
        baseNetPerSqm: z.coerce.number().min(0),
        isPricingPublished: z.boolean(),
    }),
    tags: z.array(z.string().uuid()),
    note: z.string(),
    hotel_note: z.string(),
    relatedProducts: z.array(z.string().uuid()),
});

const getInitialFormDataFromProduct = (product, defaultStatus) => {
    return {
        name: product.name_full,
        type: product.type?.id || "",
        productNumber: product.product_number || "",
        floor: product.floor || 0,
        num_rooms: product.num_rooms || 1,
        style: product.style?.id,
        status: getProductStatusId(product, defaultStatus),
        sideEast: product.orientation.east || false,
        sideWest: product.orientation.west || false,
        sideNorth: product.orientation.north || false,
        sideSouth: product.orientation.south || false,
        footage: product.footage || 0.0,
        footageFinal: product.footage_postconstruction ?? null,
        kind: product.kind?.id || "",
        pricing: {
            vat:
                typeof product.pricing?.vat_proc === "string"
                    ? Number.parseFloat(product.pricing.vat_proc) || 0.0
                    : product.pricing?.vat_proc || 0.0,
            maxDiscount:
                typeof product.pricing?.discount_max === "string"
                    ? Number.parseFloat(product.pricing.discount_max) || 0.0
                    : product.pricing?.discount_max || 0.0,
            listedNetPerSqm: Number.parseFloat(product.pricing?.listed_net_per_m2) || 0.0,
            minNetPerSqm: Number.parseFloat(product.pricing?.min_net_per_m2) || 0.0,
            baseNetPerSqm: Number.parseFloat(product.pricing?.base_net_per_m2) || 0.0,
            isPricingPublished: product.pricing?.pricing_published || false,
        },
        tags: product.tags.map((t) => t.id) || [],
        note: product.note || "",
        hotel_note: product.hotel_note || "",
        relatedProducts: product.children?.map((p) => p.id) || [],
    };
};

const getEmptyFormValues = () => ({
    name: null,
    type: null,
    productNumber: null,
    floor: null,
    num_rooms: null,
    status: null,
    style: null,
    sideEast: false,
    sideWest: false,
    sideNorth: false,
    sideSouth: false,
    footage: null,
    footageFinal: null,
    kind: null,

    pricing: {
        vat: 0.0,
        maxDiscount: 0.0,
        listedNetPerSqm: 0.0,
        minNetPerSqm: 0.0,
        baseNetPerSqm: 0.0,
        isPricingPublished: false,
    },

    tags: [],
    note: null,
    hotel_note: null,
    relatedProducts: [],
});

const productParametersFieldNames = {
    name: "name",
    type: "type",
    style: "style",
    productNumber: "productNumber",
    floor: "floor",
    num_rooms: "num_rooms",
    status: "status",
    sides: {
        east: "sideEast",
        west: "sideWest",
        north: "sideNorth",
        south: "sideSouth",
    },
    footage: "footage",
    footageFinal: "footageFinal",
};

const multipleProductsFieldNamesToEdit = [
    "type",
    "floor",
    "num_rooms",
    "status",
    "kind",
    "note",
    "hotel_note",
]

const productPricingFieldNames = {
    kind: "kind",
    footage: "footage",
    footageFinal: "footageFinal",
    pricing: {
        maxDiscount: "pricing.maxDiscount",
        vat: "pricing.vat",
        listedNetPerSqm: "pricing.listedNetPerSqm",
        minNetPerSqm: "pricing.minNetPerSqm",
        baseNetPerSqm: "pricing.baseNetPerSqm",
    },
};
